/**
 * Immersion Calendar — month grid with click-through to candidate profile.
 *
 * Visible to: admin (all), referent (their assigned immersions), pre_recruteur
 * (immersions of candidates they pre-recruited). Composite users see the union.
 *
 * Status colors:
 *   • confirmed → emerald  (candidate clicked magic link)
 *   • pending   → amber
 *   • done      → emerald solid
 *   • no_show   → red, strike-through ("barré")
 *   • cancelled → red, strike-through
 */
import { motion } from "framer-motion";
import {
    AlertCircle,
    CalendarDays,
    Check,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    Clock,
    Loader2,
    Mail,
    MapPin,
    Phone,
    RotateCcw,
    Trash2,
    Trophy,
    User as UserIcon,
    UserX,
    X,
    XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { api } from "../lib/api";
import { formatInParis, parisParts } from "../lib/tz";
import ScheduleImmersionModal from "../components/candidates/ScheduleImmersionModal";
import SendMessageModal from "../components/messaging/SendMessageModal";
import ConfirmModal from "../components/common/ConfirmModal";
import PostImmersionDecisionModal from "../components/candidates/PostImmersionDecisionModal";
import LastDecisionBanner from "../components/candidates/LastDecisionBanner";


const STATUS_META = {
    confirmed: {
        label: "Confirmé",
        chip: "bg-emerald-100 border-emerald-300 text-emerald-800",
        dot: "bg-emerald-500",
        ring: "ring-emerald-500/30",
        Icon: Check,
        struck: false,
    },
    pending: {
        label: "En attente",
        chip: "bg-amber-100 border-amber-300 text-amber-900",
        dot: "bg-amber-500",
        ring: "ring-amber-500/30",
        Icon: Clock,
        struck: false,
    },
    done: {
        label: "Fait",
        chip: "bg-emerald-500 border-emerald-600 text-white",
        dot: "bg-emerald-600",
        ring: "ring-emerald-500/30",
        Icon: Trophy,
        struck: false,
    },
    no_show: {
        label: "No-show",
        chip: "bg-red-50 border-red-300 text-red-700",
        dot: "bg-red-500",
        ring: "ring-red-500/30",
        Icon: UserX,
        struck: true,
    },
    cancelled: {
        // Set when the candidate ended up REJECTED post-scheduling. We label it
        // "Refusé" (not "Annulé") because the decision came from the team —
        // saying "Annulé" wrongly suggests the candidate cancelled themselves.
        label: "Refusé",
        chip: "bg-red-50 border-red-300 text-red-700",
        dot: "bg-red-500",
        ring: "ring-red-500/30",
        Icon: UserX,
        struck: true,
    },
};


/* ──────────────────────────────────────────── Date helpers (pure FR locale) */

const FR_MONTHS = [
    "janvier", "février", "mars", "avril", "mai", "juin",
    "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];
const FR_WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
function startOfNextMonth(d) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 1);
}
function addDays(d, n) {
    const c = new Date(d);
    c.setDate(c.getDate() + n);
    return c;
}
function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
}
/** First-day-Monday calendar grid for `month` — always 6 rows × 7 cols (42 days). */
function buildMonthGrid(month) {
    const first = startOfMonth(month);
    // weekday Mon=0 … Sun=6
    const weekdayMonFirst = (first.getDay() + 6) % 7;
    const gridStart = addDays(first, -weekdayMonFirst);
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}


/* ──────────────────────────────────────────── The page */

export default function ImmersionCalendar() {
    const navigate = useNavigate();
    const [month, setMonth] = useState(() => startOfMonth(new Date()));
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [marking, setMarking] = useState(false);
    // Reschedule modal — pre-filled from the entry the user clicked
    const [rescheduling, setRescheduling] = useState(null); // { candidateId, name } | null
    // After a successful reschedule, this holds the candidate so we can open
    // the SendMessageModal with the recap-interview template.
    const [notifyAfterSchedule, setNotifyAfterSchedule] = useState(null);
    // Confirmation modal for cancel
    const [cancelling, setCancelling] = useState(null);     // { candidateId, name } | null
    // Post-immersion decision modal (validate / reject)
    const [decidingFor, setDecidingFor] = useState(null);   // candidate object | null

    const load = useCallback(async () => {
        setLoading(true);
        // Fetch one extra week before & after so chips on out-of-month grid cells still show
        const from = addDays(startOfMonth(month), -7);
        const to = addDays(startOfNextMonth(month), 7);
        try {
            const { data } = await api.get(`/immersions/calendar`, {
                params: { from: from.toISOString(), to: to.toISOString() },
            });
            setEntries(data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur de chargement");
        } finally {
            setLoading(false);
        }
    }, [month]);

    useEffect(() => { load(); }, [load]);

    const grid = useMemo(() => buildMonthGrid(month), [month]);
    const today = useMemo(() => new Date(), []);

    // Index entries by yyyy-mm-dd Paris day key. The immersion physically
    // happens in Europe/Paris, so the CALENDAR day must be the Paris day
    // regardless of the viewer's browser timezone. Otherwise a 22h Paris
    // immersion would land on the next day for a viewer east of Paris.
    const entriesByDay = useMemo(() => {
        const map = new Map();
        entries.forEach((e) => {
            const p = parisParts(e.scheduled_at);
            if (!p) return;
            const key = `${p.year}-${p.month - 1}-${p.day}`;
            if (!map.has(key)) map.set(key, []);
            // Preserve the raw UTC instant for sorting AND stash the Paris
            // wall clock parts for downstream display (avoids re-computing).
            map.get(key).push({ ...e, _dt: new Date(e.scheduled_at), _parisParts: p });
        });
        // sort each day by time
        for (const list of map.values()) list.sort((a, b) => a._dt - b._dt);
        return map;
    }, [entries]);

    /* Totals per status_kind for the header strip */
    const totals = useMemo(() => {
        const t = { confirmed: 0, pending: 0, done: 0, no_show: 0, cancelled: 0 };
        entries.forEach((e) => {
            if (t[e.status_kind] !== undefined) t[e.status_kind] += 1;
        });
        return t;
    }, [entries]);

    async function markNoShow(candidateId) {
        if (marking) return;
        setMarking(true);
        try {
            await api.post(`/immersions/${candidateId}/mark-no-show`);
            toast.success("No-show enregistré");
            setSelectedEntry(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setMarking(false);
        }
    }

    async function undoNoShow(candidateId) {
        if (marking) return;
        setMarking(true);
        try {
            await api.post(`/immersions/${candidateId}/undo-no-show`);
            toast.success("No-show annulé");
            setSelectedEntry(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setMarking(false);
        }
    }

    /** Cancel a scheduled immersion to fix wrong info (referent / address /
     * phone). The candidate is put back at status=prerec_done so the
     * pre-recruteur can reprogram with the right info. */
    async function cancelImmersion(candidateId) {
        if (marking) return;
        setMarking(true);
        try {
            await api.post(`/candidates/${candidateId}/cancel-immersion`);
            toast.success("Immersion annulée — vous pouvez la reprogrammer");
            setCancelling(null);
            setSelectedEntry(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setMarking(false);
        }
    }

    /** Open the schedule modal pre-filled with the candidate's current values
     * so the pre-recruteur only edits what's wrong. We fetch the full
     * candidate doc (the entry only has light fields). */
    async function openReschedule(candidateId) {
        try {
            const { data } = await api.get(`/candidates/${candidateId}`);
            const cand = data.candidate || data; // backend can return either shape
            setSelectedEntry(null);
            setRescheduling({ candidate: cand });
        } catch (e) {
            toast.error("Impossible de charger le candidat");
        }
    }

    /** Open the post-immersion decision modal — validate or reject after
     * the immersion has happened. Needs the full candidate dict so the
     * modal can render the name + persist the action. */
    async function openDecision(candidateId) {
        try {
            const { data } = await api.get(`/candidates/${candidateId}`);
            const cand = data.candidate || data;
            setSelectedEntry(null);
            setDecidingFor(cand);
        } catch (e) {
            toast.error("Impossible de charger le candidat");
        }
    }

    return (
        <div className="space-y-6" data-testid="immersion-calendar">
            {/* Header */}
            <header>
                <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                    <CalendarDays className="h-3 w-3" /> Calendrier terrain
                </div>
                <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-shariti-black" data-testid="calendar-title">
                    Immersions programmées
                </h1>
                <p className="mt-2 text-sm font-medium text-slate-500">
                    Visualisez vos entretiens immersifs à venir, l'engagement des candidats et les no-shows passés.
                </p>
            </header>

            {/* Month navigator + legend */}
            <div className="card-soft p-4 flex flex-wrap items-center gap-3" data-testid="calendar-navigator">
                <button
                    onClick={() => setMonth(addDays(startOfMonth(month), -1))}
                    data-testid="calendar-prev"
                    className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-600 hover:bg-shariti-orange-soft hover:text-shariti-orange transition"
                    title="Mois précédent"
                >
                    <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="text-xl md:text-2xl font-black tracking-tight text-shariti-black capitalize">
                    {FR_MONTHS[month.getMonth()]} {month.getFullYear()}
                </div>
                <button
                    onClick={() => setMonth(startOfNextMonth(month))}
                    data-testid="calendar-next"
                    className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-600 hover:bg-shariti-orange-soft hover:text-shariti-orange transition"
                    title="Mois suivant"
                >
                    <ChevronRight className="h-4 w-4" />
                </button>
                <button
                    onClick={() => setMonth(startOfMonth(new Date()))}
                    data-testid="calendar-today"
                    className="ml-1 px-3 h-9 inline-flex items-center gap-1.5 rounded-full bg-shariti-black text-white text-xs font-extrabold uppercase tracking-wider hover:bg-slate-800 transition"
                >
                    Aujourd'hui
                </button>

                <div className="ml-auto flex flex-wrap items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    {Object.entries(STATUS_META).map(([k, m]) => (
                        <div key={k} className="inline-flex items-center gap-1.5" data-testid={`calendar-legend-${k}`}>
                            <span className={`h-2 w-2 rounded-full ${m.dot} ${m.struck ? "opacity-70" : ""}`} />
                            {m.label}
                            <span className="ml-0.5 text-slate-700 font-extrabold">{totals[k] || 0}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* The month grid — hidden on mobile (replaced by an agenda list below) */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-soft p-2 md:p-3 relative hidden md:block"
                data-testid="calendar-grid"
            >
                {loading && (
                    <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10 rounded-3xl">
                        <Loader2 className="h-6 w-6 animate-spin text-shariti-orange" />
                    </div>
                )}

                {/* Weekday header */}
                <div className="grid grid-cols-7 gap-1 mb-1 px-1">
                    {FR_WEEKDAYS.map((d) => (
                        <div key={d} className="text-center text-[10px] font-extrabold uppercase tracking-widest text-slate-400 py-1.5">
                            {d}
                        </div>
                    ))}
                </div>

                {/* 6×7 grid */}
                <div className="grid grid-cols-7 gap-1">
                    {grid.map((day) => {
                        const inMonth = day.getMonth() === month.getMonth();
                        const isToday = isSameDay(day, today);
                        const key = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
                        const dayEntries = entriesByDay.get(key) || [];

                        return (
                            <div
                                key={key}
                                data-testid={`calendar-day-${day.getDate()}`}
                                className={`min-h-[110px] rounded-xl border-2 p-1.5 flex flex-col gap-1 transition ${inMonth
                                    ? isToday
                                        ? "bg-shariti-orange-soft border-shariti-orange/40"
                                        : "bg-white border-shariti-gray/50"
                                    : "bg-shariti-cream/50 border-transparent opacity-50"
                                    }`}
                            >
                                <div className={`flex items-center justify-between px-1 ${isToday ? "text-shariti-orange" : inMonth ? "text-slate-700" : "text-slate-400"}`}>
                                    <span className={`text-xs font-extrabold ${isToday ? "" : ""}`}>{day.getDate()}</span>
                                    {dayEntries.length > 0 && (
                                        <span className="text-[9px] font-bold text-slate-400">
                                            {dayEntries.length}
                                        </span>
                                    )}
                                </div>

                                {dayEntries.slice(0, 3).map((e) => {
                                    const meta = STATUS_META[e.status_kind] || STATUS_META.pending;
                                    const hh = String(e._parisParts.hour).padStart(2, "0");
                                    const mm = String(e._parisParts.minute).padStart(2, "0");
                                    return (
                                        <button
                                            key={e.candidate_id + e.scheduled_at}
                                            onClick={() => setSelectedEntry(e)}
                                            data-testid={`calendar-chip-${e.candidate_id}`}
                                            className={`group relative w-full text-left px-1.5 py-1 rounded-md border ${meta.chip} hover:ring-2 ${meta.ring} transition leading-tight overflow-hidden`}
                                        >
                                            <div className={`flex items-center gap-1 text-[10px] font-bold ${meta.struck ? "line-through opacity-80" : ""}`}>
                                                <span className="shrink-0">{hh}h{mm}</span>
                                                <span className="truncate">{e.candidate_name}</span>
                                            </div>
                                            {e.location && (
                                                <div className={`text-[9px] font-medium truncate ${meta.struck ? "line-through opacity-70" : "opacity-80"}`}>
                                                    {e.location}
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}

                                {dayEntries.length > 3 && (
                                    <button
                                        onClick={() => setSelectedEntry({ _bulk: dayEntries, day })}
                                        data-testid={`calendar-more-${key}`}
                                        className="text-[10px] font-bold text-shariti-orange hover:underline px-1 pt-0.5"
                                    >
                                        +{dayEntries.length - 3} de plus
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </motion.div>

            {/* Mobile agenda list — replaces the squeezed month grid on small screens */}
            <MobileAgenda
                month={month}
                entries={entries}
                loading={loading}
                onSelect={setSelectedEntry}
            />

            {/* Empty state */}
            {!loading && entries.length === 0 && (
                <div className="card-soft p-8 text-center text-slate-500" data-testid="calendar-empty">
                    <CalendarDays className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                    <div className="text-sm font-bold text-slate-600">Aucune immersion sur cette période</div>
                    <div className="text-xs mt-1">Programmez un entretien immersif depuis la fiche d'un candidat pour qu'il apparaisse ici.</div>
                </div>
            )}

            {/* Detail panel (single entry or bulk-day) */}
            {selectedEntry && (
                <DetailDrawer
                    entry={selectedEntry}
                    marking={marking}
                    onClose={() => setSelectedEntry(null)}
                    onNavigate={(id) => navigate(`/candidats/${id}`)}
                    onMarkNoShow={markNoShow}
                    onUndoNoShow={undoNoShow}
                    onReschedule={(candidateId, name) => openReschedule(candidateId)}
                    onAskCancel={(candidateId, name) => setCancelling({ candidateId, name })}
                    onDecide={(candidateId) => openDecision(candidateId)}
                />
            )}

            {/* Reschedule modal — opens the existing ScheduleImmersionModal
                pre-populated from the previous values; on save the calendar
                refreshes. */}
            {rescheduling && (
                <ScheduleImmersionModal
                    candidate={rescheduling.candidate}
                    onCancel={() => setRescheduling(null)}
                    onSuccess={async (updated, opts) => {
                        const c = rescheduling.candidate;
                        setRescheduling(null);
                        await load();
                        toast.success("Immersion mise à jour");
                        // Auto-open the SendMessageModal so the candidate gets
                        // the WhatsApp recap with the NEW info (date / lieu /
                        // référent / téléphone). Without this, the candidate
                        // believes the old info is still valid.
                        if (opts?.notifyAfter !== false && c?.phone) {
                            // Merge the updated fields so the preview renders the
                            // FRESH context, not the stale one (the modal pulls
                            // context via /messages/preview which reads the DB,
                            // so we can safely pass the loaded `c` here).
                            setNotifyAfterSchedule({ ...c, ...(updated || {}) });
                        }
                    }}
                />
            )}

            {/* Auto-WhatsApp recap after reschedule */}
            {notifyAfterSchedule && (
                <SendMessageModal
                    candidate={notifyAfterSchedule}
                    defaultCategory="recap_interview"
                    onClose={() => setNotifyAfterSchedule(null)}
                    onSent={() => setNotifyAfterSchedule(null)}
                />
            )}

            {/* Cancel confirmation */}
            {cancelling && (
                <ConfirmModal
                    title="Annuler cette immersion ?"
                    body={
                        <>
                            L&apos;immersion programmée pour <strong>{cancelling.name}</strong> sera annulée.
                            Le candidat repassera à l&apos;état <em>Pré-rec validé</em> pour pouvoir être
                            reprogrammé avec les bonnes informations (référent, adresse, téléphone).
                            <br /><br />
                            Le référent précédemment assigné recevra une notification.
                        </>
                    }
                    confirmLabel="Annuler l'immersion"
                    cancelLabel="Garder l'immersion"
                    tone="red"
                    onConfirm={() => cancelImmersion(cancelling.candidateId)}
                    onCancel={() => setCancelling(null)}
                    testid="confirm-cancel-immersion"
                />
            )}

            {/* Post-immersion decision modal — validate or reject */}
            {decidingFor && (
                <PostImmersionDecisionModal
                    candidate={decidingFor}
                    onClose={() => setDecidingFor(null)}
                    onDone={async () => {
                        setDecidingFor(null);
                        await load();
                    }}
                />
            )}
        </div>
    );
}


/* ──────────────────────────────────────────── Right-side drawer */

function DetailDrawer({ entry, marking, onClose, onNavigate, onMarkNoShow, onUndoNoShow, onReschedule, onAskCancel, onDecide }) {
    const isBulk = !!entry._bulk;
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-40 bg-shariti-black/30 backdrop-blur-sm flex justify-end"
            onClick={onClose}
            data-testid="calendar-drawer"
        >
            <motion.aside
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto"
            >
                <div className="sticky top-0 bg-white border-b border-shariti-gray px-5 py-4 flex items-center justify-between">
                    <div className="text-sm font-extrabold text-shariti-black">
                        {isBulk
                            ? `Immersions du ${entry.day.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}`
                            : "Détail de l'immersion"}
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="drawer-close"
                        className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-shariti-red hover:bg-red-50 transition"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="p-5 space-y-3">
                    {(isBulk ? entry._bulk : [entry]).map((e) => (
                        <SingleEntryCard
                            key={e.candidate_id + e.scheduled_at}
                            entry={e}
                            marking={marking}
                            onNavigate={onNavigate}
                            onMarkNoShow={onMarkNoShow}
                            onUndoNoShow={onUndoNoShow}
                            onReschedule={onReschedule}
                            onAskCancel={onAskCancel}
                            onDecide={onDecide}
                        />
                    ))}
                </div>
            </motion.aside>
        </motion.div>
    );
}

function SingleEntryCard({ entry, marking, onNavigate, onMarkNoShow, onUndoNoShow, onReschedule, onAskCancel, onDecide }) {
    const meta = STATUS_META[entry.status_kind] || STATUS_META.pending;
    const dt = new Date(entry.scheduled_at);
    const now = new Date();
    // Compare "same day" in Europe/Paris — otherwise a viewer east/west of
    // Paris would see the "same day" boundary shift by their tz offset.
    const dtP = parisParts(dt);
    const nowP = parisParts(now);
    const sameDay = dtP && nowP
        && dtP.year === nowP.year
        && dtP.month === nowP.month
        && dtP.day === nowP.day;
    // No-show is allowed once the immersion is today or past, while it's still
    // pending/confirmed. The referent shouldn't wait the day after to mark it.
    const canMarkNoShow = (sameDay || dt < now) && (entry.status_kind === "pending" || entry.status_kind === "confirmed");
    const isNoShow = entry.status_kind === "no_show";

    return (
        <div className="card-soft p-4 space-y-3" data-testid="drawer-entry">
            <div className="flex items-start gap-3">
                {entry.candidate_avatar ? (
                    <img src={entry.candidate_avatar} alt="" className="h-12 w-12 rounded-2xl object-cover shrink-0" />
                ) : (
                    <div className="h-12 w-12 rounded-2xl bg-shariti-orange-soft text-shariti-orange font-extrabold text-base flex items-center justify-center shrink-0">
                        {entry.candidate_name?.[0] || "?"}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <button
                        onClick={() => onNavigate(entry.candidate_id)}
                        data-testid={`drawer-open-${entry.candidate_id}`}
                        className={`block text-left font-extrabold text-shariti-black hover:text-shariti-orange truncate transition ${meta.struck ? "line-through opacity-80" : ""}`}
                    >
                        {entry.candidate_name}
                    </button>
                    {entry.layan_offer_title && (
                        <div className="text-[11px] font-medium text-slate-500 truncate mt-0.5">
                            {entry.layan_offer_title}
                        </div>
                    )}
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border ${meta.chip}`}>
                    {meta.label}
                </span>
            </div>

            <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2 text-slate-600">
                    <CalendarDays className="h-3.5 w-3.5 text-shariti-orange shrink-0" />
                    <span className={meta.struck ? "line-through opacity-80" : ""}>
                        {formatInParis(dt, { weekday: "long", day: "numeric", month: "long" })}
                        {" · "}
                        {String(dtP?.hour ?? 0).padStart(2, "0")}h{String(dtP?.minute ?? 0).padStart(2, "0")}
                    </span>
                </div>
                {entry.location && (
                    <div className="flex items-center gap-2 text-slate-600">
                        <MapPin className="h-3.5 w-3.5 text-shariti-orange shrink-0" />
                        <span className="truncate">{entry.location}</span>
                    </div>
                )}
                {entry.referent_name && (
                    <div className="flex items-center gap-2 text-slate-600">
                        <UserIcon className="h-3.5 w-3.5 text-shariti-orange shrink-0" />
                        <span>Référent : {entry.referent_name}</span>
                    </div>
                )}
                {entry.candidate_phone && (
                    <a
                        href={`tel:${entry.candidate_phone.replace(/\s/g, "")}`}
                        className="flex items-center gap-2 text-slate-600 hover:text-shariti-orange transition"
                        data-testid={`drawer-phone-${entry.candidate_id}`}
                    >
                        <Phone className="h-3.5 w-3.5 text-shariti-orange shrink-0" />
                        {entry.candidate_phone}
                    </a>
                )}
                {entry.candidate_email && (
                    <a
                        href={`mailto:${entry.candidate_email}`}
                        className="flex items-center gap-2 text-slate-600 hover:text-shariti-orange transition break-all"
                        data-testid={`drawer-email-${entry.candidate_id}`}
                        title="Envoyer un email"
                    >
                        <Mail className="h-3.5 w-3.5 text-shariti-orange shrink-0" />
                        <span className="truncate">{entry.candidate_email}</span>
                    </a>
                )}
            </div>

            {/* Decision summary — surfaced from backend `last_decision` so we
                see immediately *who* did *what*, *when* and *why*, without
                needing to open the candidate's profile. */}
            {entry.last_decision && (
                <LastDecisionBanner decision={entry.last_decision} />
            )}

            {entry.status_kind === "pending" && entry.link_viewed_at && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-[11px] text-amber-800">
                    <AlertCircle className="h-3 w-3 inline mr-1" />
                    Le candidat a consulté son lien mais n'a pas encore confirmé.
                </div>
            )}

            <div className="flex flex-wrap gap-2 pt-2">
                <button
                    onClick={() => onNavigate(entry.candidate_id)}
                    data-testid={`drawer-cta-${entry.candidate_id}`}
                    className="btn-shariti text-xs"
                >
                    Voir la fiche
                </button>
                {/* Post-immersion decision (Valider / Refuser) — available
                    whenever the immersion is still pending/confirmed.
                    Useful in 2 scenarios: (1) referent forgot to fill the
                    scorecard after the immersion, (2) admin/pre-rec wants
                    to act immediately without going through the scorecard. */}
                {(entry.status_kind === "pending" || entry.status_kind === "confirmed") && (
                    <>
                        <button
                            onClick={() => onDecide?.(entry.candidate_id)}
                            disabled={marking}
                            data-testid={`drawer-validate-${entry.candidate_id}`}
                            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-white border border-emerald-600 font-bold px-4 py-2 text-xs hover:bg-emerald-600 transition disabled:opacity-50 shadow-btn"
                            title="Valider l'immersion (recrutement acté)"
                        >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Valider
                        </button>
                        <button
                            onClick={() => onDecide?.(entry.candidate_id)}
                            disabled={marking}
                            data-testid={`drawer-decide-reject-${entry.candidate_id}`}
                            className="inline-flex items-center gap-2 rounded-full bg-red-500 text-white border border-red-600 font-bold px-4 py-2 text-xs hover:bg-red-600 transition disabled:opacity-50 shadow-btn"
                            title="Refuser après l'immersion"
                        >
                            <XCircle className="h-3.5 w-3.5" />
                            Refuser
                        </button>
                    </>
                )}
                {/* Reschedule + Cancel — only when the immersion is still
                    upcoming / pending / confirmed (not when it's already
                    done / no-show / cancelled). Fixing wrong info on the
                    WhatsApp message is the #1 use-case. */}
                {(entry.status_kind === "pending" || entry.status_kind === "confirmed") && (
                    <>
                        <button
                            onClick={() => onReschedule?.(entry.candidate_id, entry.candidate_name)}
                            disabled={marking}
                            data-testid={`drawer-reschedule-${entry.candidate_id}`}
                            className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange border border-shariti-orange/30 font-bold px-4 py-2 text-xs hover:bg-shariti-orange hover:text-white transition disabled:opacity-50"
                            title="Modifier l'horaire, le lieu ou le référent"
                        >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Reprogrammer
                        </button>
                        <button
                            onClick={() => onAskCancel?.(entry.candidate_id, entry.candidate_name)}
                            disabled={marking}
                            data-testid={`drawer-cancel-${entry.candidate_id}`}
                            className="inline-flex items-center gap-2 rounded-full bg-white text-shariti-red border border-red-200 font-bold px-4 py-2 text-xs hover:bg-red-50 transition disabled:opacity-50"
                            title="Annuler l'immersion pour la reprogrammer plus tard"
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                            Annuler
                        </button>
                    </>
                )}
                {canMarkNoShow && !isNoShow && (
                    <button
                        onClick={() => onMarkNoShow(entry.candidate_id)}
                        disabled={marking}
                        data-testid={`drawer-mark-no-show-${entry.candidate_id}`}
                        className="inline-flex items-center gap-2 rounded-full bg-red-50 text-shariti-red border border-red-200 font-bold px-4 py-2 text-xs hover:bg-red-100 transition disabled:opacity-50"
                    >
                        <UserX className="h-3.5 w-3.5" />
                        Marquer no-show
                    </button>
                )}
                {isNoShow && (
                    <button
                        onClick={() => onUndoNoShow(entry.candidate_id)}
                        disabled={marking}
                        data-testid={`drawer-undo-no-show-${entry.candidate_id}`}
                        className="inline-flex items-center gap-2 rounded-full bg-shariti-cream text-slate-600 border border-shariti-gray font-bold px-4 py-2 text-xs hover:bg-white hover:border-shariti-orange/40 transition disabled:opacity-50"
                    >
                        Annuler le no-show
                    </button>
                )}
            </div>
        </div>
    );
}



/* ──────────────────────────────────────────── Mobile agenda (replaces the
 * tiny month grid on small screens). Groups entries by date.
 */

function formatLongDate(d) {
    return d.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
    });
}

function MobileAgenda({ month, entries, loading, onSelect }) {
    // Group entries by Paris day key (YYYY-MM-DD). Same reason as
    // `entriesByDay` above: the immersion physically happens in Paris, so
    // the calendar day must be the Paris day for every viewer.
    const byDay = entries.reduce((acc, e) => {
        const p = parisParts(e.scheduled_at);
        if (!p) return acc;
        const key = `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
        (acc[key] = acc[key] || []).push(e);
        return acc;
    }, {});

    // Keep only days that fall inside the currently navigated month.
    const monthIdx = month.getMonth();
    const monthYear = month.getFullYear();
    const daysSorted = Object.entries(byDay)
        .filter(([k]) => {
            const [y, m] = k.split("-");
            return Number(y) === monthYear && Number(m) - 1 === monthIdx;
        })
        .sort(([a], [b]) => (a < b ? -1 : 1));

    // "Today" key must also be in Paris — otherwise a viewer past midnight
    // in Paris but still yesterday in their tz would see no highlight.
    const nowParisParts = parisParts(new Date());
    const todayKey = nowParisParts
        ? `${nowParisParts.year}-${String(nowParisParts.month).padStart(2, "0")}-${String(nowParisParts.day).padStart(2, "0")}`
        : "";

    return (
        <div className="md:hidden space-y-3" data-testid="calendar-mobile-agenda">
            {loading && (
                <div className="card-soft p-6 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-shariti-orange" />
                </div>
            )}
            {!loading && daysSorted.length === 0 && (
                <div className="card-soft p-6 text-center" data-testid="mobile-agenda-empty">
                    <CalendarDays className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                    <div className="text-sm font-bold text-slate-600">Aucune immersion ce mois-ci</div>
                </div>
            )}
            {!loading && daysSorted.map(([key, dayEntries]) => {
                const day = new Date(`${key}T00:00:00`);
                const isToday = key === todayKey;
                const isPast = key < todayKey;
                return (
                    <div
                        key={key}
                        className={`card-soft p-3 ${isToday ? "ring-2 ring-shariti-orange/40" : ""}`}
                        data-testid={`mobile-agenda-day-${key}`}
                    >
                        <div className="flex items-center gap-2 pb-2 mb-2 border-b border-shariti-gray/60">
                            <div className={`h-9 w-9 rounded-2xl flex items-center justify-center font-black text-base shrink-0 ${
                                isToday
                                    ? "bg-shariti-orange text-white shadow-btn"
                                    : isPast
                                    ? "bg-slate-100 text-slate-400"
                                    : "bg-shariti-orange-soft text-shariti-orange"
                            }`}>
                                {day.getDate()}
                            </div>
                            <div className="min-w-0">
                                <div className="text-xs font-extrabold uppercase tracking-widest text-slate-600 capitalize truncate">
                                    {formatLongDate(day)}
                                </div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                    {isToday
                                        ? "Aujourd'hui"
                                        : isPast
                                        ? "Passé"
                                        : `${dayEntries.length} immersion${dayEntries.length > 1 ? "s" : ""}`}
                                </div>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            {dayEntries
                                .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
                                .map((e) => {
                                    const meta = STATUS_META[e.status_kind] || STATUS_META.pending;
                                    const Icon = meta.Icon;
                                    const tp = parisParts(e.scheduled_at);
                                    const candidateName = (e.candidate_name || "").trim() || "Candidat sans nom";
                                    return (
                                        <button
                                            key={e.candidate_id + e.scheduled_at}
                                            onClick={() => onSelect(e)}
                                            data-testid={`mobile-agenda-entry-${e.candidate_id}`}
                                            className={`w-full text-left rounded-xl border-2 p-3 transition ${meta.chip} hover:scale-[1.01]`}
                                        >
                                            {/* Row 1: candidate name BIG + time */}
                                            <div className="flex items-start gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <div className={`text-sm font-extrabold leading-tight break-words ${meta.struck ? "line-through opacity-70" : ""}`}>
                                                        {candidateName}
                                                    </div>
                                                    {e.layan_offer_title && (
                                                        <div className="mt-0.5 text-[10px] font-semibold text-slate-500 truncate">
                                                            {e.layan_offer_title}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="inline-flex items-center gap-1 text-sm font-extrabold tabular-nums shrink-0 px-2 py-0.5 rounded-full bg-white/70">
                                                    <Icon className="h-3 w-3" />
                                                    {String(tp?.hour ?? 0).padStart(2, "0")}h{String(tp?.minute ?? 0).padStart(2, "0")}
                                                </div>
                                            </div>
                                            {/* Row 2: lieu · référent */}
                                            <div className="mt-1.5 flex items-center gap-2 text-[11px] font-semibold text-slate-600">
                                                {e.location ? (
                                                    <>
                                                        <MapPin className="h-3 w-3 text-slate-400 shrink-0" />
                                                        <span className="truncate">{e.location}</span>
                                                    </>
                                                ) : (
                                                    <span className="italic text-slate-400">lieu non renseigné</span>
                                                )}
                                                {e.referent_name && (
                                                    <>
                                                        <span className="text-slate-300">·</span>
                                                        <span className="truncate">{e.referent_name}</span>
                                                    </>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
